import { Model } from 'app/model/backendmodel';
import { BinaryDataset } from 'app/model/dataset/binary.dataset';
import { HdfsStoreReadSettings } from 'app/model/pipeline/copysource/store-read-settings/hdfs-store-read-settings';
import { BinaryCopySource } from 'app/model/pipeline/copysource/binary.source';
import { Dataset } from '../../model/dataset/dataset';
import { ParameterValue } from '../../model/base/parameter-value';
import { IViolation, IModel, IAttribute } from '../../model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { format, sanitizeAndFormatString, stringIsNullOrEmpty } from './string.util';

import { Activity } from 'app/model/pipeline/activity';
import { DatasetRegistryService } from 'app/registries/dataset/dataset-registry.service';
import { TabularCopySource } from 'app/model/pipeline/copysource/tabular-type.copysource';
import { TabularTypeDataset } from 'app/model/dataset/tabular-type.dataset';
import { CopySource } from 'app/model/pipeline/copysource/copysource';
import { Named } from 'app/model/base/named';
import { SelfHostedIntegrationRuntime } from 'app/model/integrationRuntime/selfhosted.integrationRuntime';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { partitionOptionConst, FilePathMode } from 'app/registries/consts/metadata-consts';
import { FormatTypeCopySource } from 'app/model/pipeline/copysource/format-type.copysource';
import { FormatDatasetV2 } from 'app/model/dataset/dataset.v2';
import { HttpServerLocation } from 'app/model/dataset/location/http-server.location';
import { LinkedService } from 'app/model/linkedservice/linkedservice';
import { BlobTypeStoreReadSettings } from 'app/model/pipeline/copysource/store-read-settings/blob-type-store-read-settings';
import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { FilePath } from 'app/model/dataset/file-path';
import { NetezzaTableDataset } from 'app/model/dataset/netezza.dataset';
import { NetezzaCopySource } from 'app/model/pipeline/copysource/netezza.copysource';
import {
    compressionCodecType,
    DelimitedTextDataset,
    IParameterizable,
    SqlPool,
    IValidationContext,
    IntegrationRuntime,
    AzureSqlDWTableDataset,
    shellStringResources
} from 'shell-models-lib';
import { StagingSettings } from 'app/model/pipeline/staging-settings';
import { AzureStorageLinkedService } from 'app/model/linkedservice/azure-storage.linkedservice';
import { AzureBlobFSLinkedService } from 'app/model/linkedservice/azure-blob-fs.linkedservice';
import { Constants } from '../shared-constants';
import { ManagedIntegrationRuntime } from 'app/model/integrationRuntime/managed.integrationRuntime';
import { IntegrationRuntimeAdapters } from 'app/integrationruntime/integrationruntime.adapter';
import { irTypesConst, irStatusConst, irCreateType } from 'app/integrationruntime/integrationruntime.constants';
import { IntegrationRuntimeOperationsService } from 'app/apis/integration-runtime-operations-api.service';
import { FileServerStoreReadSettings } from 'app/model/pipeline/copysource/store-read-settings/file-server-store-read-settings';
import { missingQuery, hasTableNameOrTable } from 'app/model/dataset/tabular-type-utils';
import { OrcDataset } from 'app/model/dataset/orc.dataset';
import { LoggingLocation } from 'app/model/pipeline/logging-location';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { SqlDWSink } from 'app/model/pipeline/copysink/sqldw.copysink';
import { LookupActivity } from 'app/model/pipeline/lookup.activity';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';
import { LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { compareDetailVersion, getMinVersion, opdgInPipelineVersion } from './version.check.util';
import { compact } from 'lodash';
import { isTableNameRequired } from './dataset-utils';

interface IRuntimeIRMap {
    ir: IntegrationRuntime;
    irType: irCreateType;
}

export function validateEntityParameters(
    entity: IParameterizable,
    parameters: ParameterValue[],
    activity: Named,
    externalEntity?: Named
): IViolation[] | undefined {
    const violations: IViolation[] = [];
    if (entity != null && entity.parameters != null && parameters != null) {
        for (const p of entity.parameters) {
            if (p.name != null && p.name !== '' && (p.defaultValue == null || p.defaultValue === '')) {
                const parameterValue = parameters.find((a) => a.parameter && a.parameter.name === p.name);
                if (
                    parameterValue == null ||
                    ((parameterValue.value == null || parameterValue.value === '') &&
                        !ParameterValue.VALUE.templateValue(parameterValue))
                ) {
                    violations.push({
                        context: externalEntity
                            ? {
                                parameter: p,
                                dataset: entity,
                                externalEntity
                            }
                            : p,
                        message: () => sanitizeAndFormatString(stringResources.Dataset_ParameterValidationError, p.name)
                    });
                }
            }
        }
        for (const pValue of parameters) {
            if (pValue.parameter) {
                const datasetParam = entity.parameters.find((a) => pValue.parameter.name === a.name);
                if (!datasetParam) {
                    violations.push({
                        context: activity,
                        message: () =>
                            sanitizeAndFormatString(
                                stringResources.Dataset_ParameterValidationMismatchError,
                                activity.name
                            )
                    });
                    break;
                }
            }
        }
    }
    return violations;
}

function getSHIRReference(linkedServices: {
    sourceLs?: LinkedService;
    sourceDMTSCredentialLs?: LinkedService;
    sinkLs?: LinkedService;
    sinkDMTSCredentialLs?: LinkedService;
    stagingLs?: LinkedService;
}) {
    const { sourceLs, sourceDMTSCredentialLs, sinkLs, sinkDMTSCredentialLs, stagingLs } = linkedServices;
    let sourceSHIR;
    let sourceDMTSCredentialSHIR;
    let sinkSHIR;
    let sinkDMTSCredentialSHIR;
    let stagingSHIR;
    let sinkLabel;
    let irLabel;
    if (__IS_FABRIC_DPE__) {
        sourceSHIR = sourceLs?.isOPDGReferenced ? sourceLs.fabricGatewayName : undefined;
        sourceDMTSCredentialSHIR = sourceDMTSCredentialLs?.isOPDGReferenced ? sourceDMTSCredentialLs.fabricGatewayName : undefined;
        sinkSHIR = sinkLs?.isOPDGReferenced ? sinkLs.fabricGatewayName : undefined;
        sinkDMTSCredentialSHIR = sinkDMTSCredentialLs?.isOPDGReferenced ? sinkDMTSCredentialLs.fabricGatewayName : undefined;
        stagingSHIR = stagingLs?.isOPDGReferenced ? stagingLs.fabricGatewayName : undefined;
        sinkLabel = stringResources.DestinationLabel;
        irLabel = shellStringResources.OnPremisesDataGateway;
    } else {
        sourceSHIR =
            sourceLs?.integrationRuntime instanceof SelfHostedIntegrationRuntime
                ? sourceLs.integrationRuntime
                : undefined;
        sinkSHIR =
            sinkLs?.integrationRuntime instanceof SelfHostedIntegrationRuntime ? sinkLs.integrationRuntime : undefined;
        stagingSHIR =
            stagingLs?.integrationRuntime instanceof SelfHostedIntegrationRuntime
                ? stagingLs.integrationRuntime
                : undefined;
        sinkLabel = stringResources.Sink;
        irLabel = stringResources.SelfHostedIntegrationRuntime;
    }
    return {
        sourceSHIR,
        sourceDMTSCredentialSHIR,
        sinkSHIR,
        sinkDMTSCredentialSHIR,
        stagingSHIR,
        sinkLabel,
        irLabel
    };
}

function getVnetIRReference(linkedServices: {
    sourceLs?: LinkedService;
    sourceDMTSCredentialLs?: LinkedService;
    sinkLs?: LinkedService;
    sinkDMTSCredentialLs?: LinkedService;
    stagingLs?: LinkedService;
}) {
    const { sourceLs, sourceDMTSCredentialLs, sinkLs, sinkDMTSCredentialLs, stagingLs } = linkedServices;
    let sourceVnetIR;
    let sourceDMTSCredentialVnetIR;
    let sinkVnetIR;
    let sinkDMTSCredentialVnetIR;
    let stagingVnetIR;
    if (__IS_FABRIC_DPE__) {
        sourceVnetIR = sourceLs?.isVnetReferenced ? sourceLs.fabricGatewayName : undefined;
        sourceDMTSCredentialVnetIR = sourceDMTSCredentialLs?.isVnetReferenced ? sourceDMTSCredentialLs.fabricGatewayName : undefined;
        sinkVnetIR = sinkLs?.isVnetReferenced ? sinkLs.fabricGatewayName : undefined;
        sinkDMTSCredentialVnetIR = sinkDMTSCredentialLs?.isVnetReferenced ? sinkDMTSCredentialLs.fabricGatewayName : undefined;
        stagingVnetIR = stagingLs?.isVnetReferenced ? stagingLs.fabricGatewayName : undefined;
    }
    return {
        sourceVnetIR,
        sourceDMTSCredentialVnetIR,
        sinkVnetIR,
        sinkDMTSCredentialVnetIR,
        stagingVnetIR
    };
}

async function validateIRReference(
    linkedServices: {
        sourceLs?: LinkedService;
        sourceDMTSCredentialLs?: LinkedService;
        sinkLs?: LinkedService;
        sinkDMTSCredentialLs?: LinkedService;
        stagingLs?: LinkedService;
    },
    context: IValidationContext,
    ownedActivity?: CopyActivity
): Promise<IViolation[] | undefined> {
    const { sourceSHIR, sourceDMTSCredentialSHIR, sinkSHIR, sinkDMTSCredentialSHIR, stagingSHIR, sinkLabel, irLabel } = getSHIRReference(linkedServices);
    const { sourceVnetIR, sourceDMTSCredentialVnetIR, sinkVnetIR, sinkDMTSCredentialVnetIR, stagingVnetIR } = getVnetIRReference(linkedServices);
    const { sourceLs, sourceDMTSCredentialLs, sinkLs, sinkDMTSCredentialLs, stagingLs } = linkedServices;
    const lsRegistry = await getServiceInstance<LinkedServiceRegistryService>(
        context.contextData,
        'LinkedServiceRegistryService'
    );
    const sourceLsConfig = sourceLs
        ? lsRegistry.getLinkedServiceConfiguration(sourceLs.getLinkedServiceType())
        : undefined;
    const sinkLsConfig = sinkLs ? lsRegistry.getLinkedServiceConfiguration(sinkLs.getLinkedServiceType()) : undefined;
    // #1. validate only Azure IR for source & sink ls
    if (__IS_FABRIC_DPE__ && (sourceSHIR || sinkSHIR)) {
        const onlyAzureIREntity = sourceLsConfig?.onlyAzureIR
            ? {
                lsConfig: sourceLsConfig,
                directionLabel: stringResources.To,
                dataset: ownedActivity?.sourceDataset,
                attribute: ownedActivity?.sourceDataset
                    ? Metamodel.modelOf(ownedActivity.sourceDataset).attribute('linkedService')
                    : undefined
            }
            : sinkLsConfig?.onlyAzureIR
                ? {
                    lsConfig: sinkLsConfig,
                    directionLabel: stringResources.From,
                    dataset: ownedActivity?.sinkDataset,
                    attribute: ownedActivity?.sinkDataset
                        ? Metamodel.modelOf(ownedActivity.sinkDataset).attribute('linkedService')
                        : undefined
                }
                : undefined;
        if (onlyAzureIREntity) {
            return [
                {
                    message: () =>
                        format(
                            stringResources.nonOPDGtoOPDG,
                            onlyAzureIREntity.lsConfig.displayName,
                            onlyAzureIREntity.directionLabel.toLocaleLowerCase()
                        ),
                    redirectTo:
                        onlyAzureIREntity.dataset || onlyAzureIREntity.attribute
                            ? {
                                entity: onlyAzureIREntity.dataset,
                                attribute: onlyAzureIREntity.attribute
                            }
                            : undefined
                }
            ];
        }
    }

    // #2. validate same IR reference (OPDG) on sourceLs/sourceDMTSCredentialLs/sink/sinkDMTSCredentialLs/staging
    const effectiveSourceSHIR = sourceDMTSCredentialSHIR ?? sourceSHIR;
    const effectiveSinkSHIR = sinkDMTSCredentialSHIR ?? sinkSHIR;
    let errorMessage = '';
    if (effectiveSourceSHIR != null && effectiveSinkSHIR != null && effectiveSourceSHIR !== effectiveSinkSHIR) {
        // source/sink pair (even with staging, they have to be the same)
        errorMessage = format(
            stringResources.DifferentSelfhostedIRError,
            stringResources.Source_Label,
            sinkLabel.toLocaleLowerCase(),
            irLabel.toLocaleLowerCase()
        );
    } else if (effectiveSourceSHIR != null && stagingSHIR != null && effectiveSourceSHIR !== stagingSHIR) {
        // source/staging pair
        errorMessage = format(
            stringResources.DifferentSelfhostedIRError,
            stringResources.Source_Label,
            stringResources.Staging,
            irLabel.toLocaleLowerCase()
        );
    } else if (stagingSHIR != null && effectiveSinkSHIR != null && stagingSHIR !== effectiveSinkSHIR) {
        // sink/staging pair
        errorMessage = format(
            stringResources.DifferentSelfhostedIRError,
            sinkLabel,
            stringResources.Staging,
            irLabel.toLocaleLowerCase()
        );
    }
    if (errorMessage) {
        return [
            {
                context: ownedActivity,
                message: () => errorMessage
            }
        ];
    }
    // #3. validation for `allow cloud connection to be used in IR (OPDG or VNet data gateways)`
    if (__IS_FABRIC_DPE__) {
        const effectiveSourceGateway = effectiveSourceSHIR ?? sourceDMTSCredentialVnetIR ?? sourceVnetIR;
        const effectiveSinkGateway = effectiveSinkSHIR ?? sinkDMTSCredentialVnetIR ?? sinkVnetIR;
        const effectiveStagingGateway = stagingSHIR ?? stagingVnetIR;
        if (effectiveSourceGateway || effectiveSinkGateway || effectiveStagingGateway) {
            if (!effectiveSourceGateway) {
                const failingLs = [sourceDMTSCredentialLs, sourceLs].find(
                    (ls) => ls?.isExternalConnection && !ls.allowDatasourceThroughOnPremsGateway && Named.OBJECT_ID.templateValue(ls) == null
                );
                if (failingLs) {
                    const redirectAttribute = failingLs === sourceDMTSCredentialLs ? 'dmtsCredentialForArtifact' : 'linkedService';
                    return [
                        {
                            message: () =>
                                format(
                                    stringResources.AllowConnectionToBeUsedWithGatewayError,
                                    stringResources.Source_Label.toLocaleLowerCase()
                                ),
                            redirectTo: ownedActivity?.sourceDataset
                                ? {
                                    entity: ownedActivity.sourceDataset,
                                    attribute: Metamodel.modelOf(ownedActivity.sourceDataset).attribute(
                                        redirectAttribute
                                    )
                                }
                                : undefined
                        }
                    ];
                }
            }
            if (!effectiveSinkGateway) {
                const failingLs = [sinkDMTSCredentialLs, sinkLs].find(
                    (ls) => ls?.isExternalConnection && !ls.allowDatasourceThroughOnPremsGateway && Named.OBJECT_ID.templateValue(ls) == null
                );
                if (failingLs) {
                    const redirectAttribute = failingLs === sinkDMTSCredentialLs ? 'dmtsCredentialForArtifact' : 'linkedService';
                    return [
                        {
                            message: () =>
                                format(
                                    stringResources.AllowConnectionToBeUsedWithGatewayError,
                                    stringResources.DestinationLabel.toLocaleLowerCase()
                                ),
                            redirectTo: ownedActivity?.sinkDataset
                                ? {
                                    entity: ownedActivity.sinkDataset,
                                    attribute: Metamodel.modelOf(ownedActivity.sinkDataset).attribute(
                                        redirectAttribute
                                    )
                                }
                                : undefined
                        }
                    ];
                }
            }
            if (
                !effectiveStagingGateway &&
                stagingLs?.isExternalConnection &&
                !stagingLs.allowDatasourceThroughOnPremsGateway &&
                Named.OBJECT_ID.templateValue(stagingLs) == null
            ) {
                return [
                    {
                        context: ownedActivity,
                        redirectTo: ownedActivity
                            ? {
                                attribute: Metamodel.modelOf(ownedActivity).attribute('stagingSettings')
                            }
                            : undefined,
                        message: () =>
                            format(stringResources.AllowConnectionToBeUsedWithGatewayError, stringResources.Staging)
                    }
                ];
            }

            if (sourceSHIR || sourceDMTSCredentialSHIR || sinkSHIR || sinkDMTSCredentialSHIR || stagingSHIR) {
                // #4 validate OPDG version
                const minOpdgVersion = getMinVersion(
                    compact([
                        ...(sourceLs?.memberGateways?.map((item) => item.version) || []),
                        ...(sourceDMTSCredentialLs?.memberGateways?.map((item) => item.version) || []),
                        ...(sinkLs?.memberGateways?.map((item) => item.version) || []),
                        ...(sinkDMTSCredentialLs?.memberGateways?.map((item) => item.version) || []),
                        ...(stagingLs?.memberGateways?.map((item) => item.version) || [])
                    ])
                );
                if (minOpdgVersion) {
                    const opdgVersionChecks = [
                        { ls: sourceLs, lsConfig: sourceLsConfig, dataset: ownedActivity?.sourceDataset, redirectAttribute: 'linkedService' },
                        { ls: sourceDMTSCredentialLs, dataset: ownedActivity?.sourceDataset, redirectAttribute: 'dmtsCredentialForArtifact' },
                        { ls: sinkLs, lsConfig: sinkLsConfig, dataset: ownedActivity?.sinkDataset, redirectAttribute: 'linkedService' },
                        { ls: sinkDMTSCredentialLs, dataset: ownedActivity?.sinkDataset, redirectAttribute: 'dmtsCredentialForArtifact' },
                    ];
                    for (const { ls, lsConfig, dataset, redirectAttribute } of opdgVersionChecks) {
                        const requiredMinVersion = ls?.isOPDGReferenced
                            ? (lsConfig?.supportedOpdgVersion ?? opdgInPipelineVersion)
                            : undefined;
                        if (requiredMinVersion && compareDetailVersion(minOpdgVersion, requiredMinVersion) < 0) {
                            return [
                                {
                                    message: () =>
                                        format(stringResources.OpdgOutdatedVersionError, lsConfig?.displayName ?? ls?.name, requiredMinVersion),
                                    redirectTo: dataset
                                        ? { entity: dataset, attribute: Metamodel.modelOf(dataset).attribute(redirectAttribute) }
                                        : undefined
                                }
                            ];
                        }
                    }
                }
            }
        }
    }
    return undefined;
}

export async function validateIR(
    linkedServices: {
        sourceLs?: LinkedService;
        sourceDMTSCredentialLs?: LinkedService;
        sinkLs?: LinkedService;
        sinkDMTSCredentialLs?: LinkedService;
        stagingLs?: LinkedService;
    },
    context: IValidationContext,
    activity?: CopyActivity
): Promise<IViolation[] | undefined> {
    const irReferenceViolations = await validateIRReference(linkedServices, context, activity);
    if (irReferenceViolations != null) {
        return irReferenceViolations;
    }
    if (activity) {
        return validateManagedVNetIRStatus(
            context,
            activity.sourceDataset,
            activity.sinkDataset,
            linkedServices.stagingLs
        );
    }
    return undefined;
}

export async function validateManagedVNetIRStatus(
    context: IValidationContext,
    sourceDataset: Dataset,
    sinkDataset?: Dataset,
    stagingLS?: LinkedService
): Promise<IViolation[] | undefined> {
    // only do this check for debug run scenario
    if (
        !(await getServiceInstance<ExposureControlService>(context.contextData, 'ExposureControlService'))
            .isVNetPresent$.value ||
        context?.isPublish
    ) {
        return undefined;
    }
    const vnetIRs: ManagedIntegrationRuntime[] = getRuntimeManagedVNetIR(sourceDataset, sinkDataset, stagingLS);
    if (vnetIRs == null || vnetIRs.length === 0) {
        return undefined;
    }
    const irOperationService = await getServiceInstance<IntegrationRuntimeOperationsService>(
        context.contextData,
        'IntegrationRuntimeOperationsService'
    );
    const violations: IViolation[] = [];
    for (const vnetIR of vnetIRs) {
        const irStatus: Model.IntegrationRuntimeStatusResponse = await irOperationService.getStatusAsync(vnetIR.name);
        const irStatusTypeProperties: Model.ManagedIntegrationRuntimeStatusTypeProperties =
            irStatus.properties.typeProperties;
        if (irStatusTypeProperties.dataMovementState !== irStatusConst.Succeeded) {
            violations.push({
                context: vnetIR,
                message: () => sanitizeAndFormatString(stringResources.ManagedVNetNotReadyForDataMovement, vnetIR.name),
                doNotAutomaticallyCorrect: true
            });
        }
    }
    return violations;
}

export async function validateCopySource(
    activity: CopyActivity | LookupActivity,
    source: CopySource,
    sourceDataset: Dataset,
    sinkDataset: Dataset,
    context: IValidationContext
): Promise<IViolation[] | undefined> {
    const violations: IViolation[] = [];
    if (source != null && sourceDataset != null) {
        // v2 validation
        // 1. wilcard filename or dataset filename, at least one is not empty
        if (
            source instanceof FormatTypeCopySource &&
            sourceDataset instanceof FormatDatasetV2 &&
            sourceDataset.location &&
            !(sourceDataset.location instanceof HttpServerLocation) &&
            !(sourceDataset instanceof BinaryDataset) &&
            source.storeSettings instanceof BlobTypeStoreReadSettings
        ) {
            const filePathMode = source.storeSettings.filePathMode;
            if (
                __IS_FABRIC_DPE__ ||
                ([
                    'AzureBlobStorageReadSettings',
                    'AmazonS3ReadSettings',
                    'GoogleCloudStorageReadSettings',
                    'AzureFileStorageReadSettings'
                ].includes(Metamodel.modelOf(source.storeSettings).name) &&
                    filePathMode === FilePathMode.PREFIX) ||
                [FilePathMode.LIST, FilePathMode.FILEFILTER, FilePathMode.NameRange].includes(filePathMode) ||
                (activity instanceof CopyActivity &&
                    sinkDataset instanceof AzureSqlDWTableDataset &&
                    (activity?.copySink as SqlDWSink).copyMethod === 'copyCommand' &&
                    !activity.enableStaging) // In ADF, direct copy doesn't have this limitation
            ) {
                // bypass this scenario.
            } else {
                const wildcardAttr = Metamodel.modelOf(source.storeSettings).attribute('wildcardFileName');
                const fileNameAttr = Metamodel.modelOf(sourceDataset.location).attribute('fileName');
                if (
                    wildcardFileNameIsNull(source.storeSettings) &&
                    stringIsNullOrEmpty(sourceDataset.location.getLocation().fileName) &&
                    fileNameAttr &&
                    !fileNameAttr.templateValue(sourceDataset.location)
                ) {
                    return [
                        {
                            context: activity,
                            message: () =>
                                sanitizeAndFormatString(
                                    stringResources.PropertyRequired_InActivity_WildcardFilename,
                                    sourceDataset.name,
                                    activity.name
                                ),
                            redirectTo: {
                                entity: activity,
                                attribute: wildcardAttr // todo, can't focus on wildcardFileName if it is not displayed in UI
                            }
                        }
                    ];
                }
            }
        }

        if (
            source instanceof BinaryCopySource &&
            source.storeSettings instanceof HdfsStoreReadSettings &&
            source.storeSettings.distCpSetting.useDistCp
        ) {
            if (!wildcardFileNameIsNull(source.storeSettings)) {
                violations.push({
                    context: activity,
                    message: () => stringResources.WildcardFileNameUnsupportedForDistCp,
                    redirectTo: {
                        entity: activity,
                        attribute: HdfsStoreReadSettings.WILDCARD_FILENAME
                    }
                });
            }
            if (!wildcardFolderPathIsNull(source.storeSettings)) {
                violations.push({
                    context: activity,
                    message: () => stringResources.WildcardFolderPathUnsupportedForDistCp,
                    redirectTo: {
                        entity: activity,
                        attribute: HdfsStoreReadSettings.WILDCARD_FOLDERPATH
                    }
                });
            }
            if (!modifiedTimeIsNull(source.storeSettings, true)) {
                violations.push({
                    context: activity,
                    message: () => stringResources.LastModifiedTimeStartUnsupportedForDistCp,
                    redirectTo: {
                        entity: activity,
                        attribute: HdfsStoreReadSettings.MODIFID_DATETIME_START
                    }
                });
            }

            if (source.storeSettings.filePathMode !== FilePathMode.LIST) {
                if (
                    !source.storeSettings.recursive &&
                    !HdfsStoreReadSettings.RECURSIVE_SETTING.templateValue(source.storeSettings)
                ) {
                    violations.push({
                        context: activity,
                        message: () => stringResources.RecursiveRequiredForDistCp,
                        redirectTo: {
                            entity: activity,
                            attribute: HdfsStoreReadSettings.RECURSIVE_SETTING
                        }
                    });
                }
            }
        }

        const datasetRegistry = await getServiceInstance<DatasetRegistryService>(
            context.contextData,
            'DatasetRegistryService'
        );
        const datasetConfig = datasetRegistry.getDatasetConfiguration(Metamodel.modelOf(sourceDataset).name);
        if (datasetConfig.datasetKind === 'table' || datasetConfig.datasetKind === 'hierarchical') {
            if (!isTableNameRequired(datasetConfig, sourceDataset) && !datasetConfig.disableTableName) {
                // otherwise, it will be validated in dataset
                if (
                    source instanceof TabularCopySource &&
                    source.mode === 'table' &&
                    !hasTableNameOrTable(sourceDataset as TabularTypeDataset)
                ) {
                    violations.push({
                        context: activity,
                        messageType: 'missing',
                        message: () =>
                            format(
                                stringResources.PropertyRequired_InActivity_Template,
                                __IS_FABRIC_DPE__
                                    ? `${stringResources.Source_Label} ${(datasetConfig.tableNameDisplay || stringResources.ModelName_Table).toLowerCase()}`
                                    : datasetConfig.tableNameDisplay || stringResources.ModelName_Table,
                                Metamodel.modelOf(activity).display[0]
                            ),
                        redirectTo: {
                            entity: sourceDataset,
                            attribute: !datasetConfig.splitTablePath
                                ? TabularTypeDataset.TABLE_NAME
                                : TabularTypeDataset.TABLE
                        }
                    });
                }

                if (
                    source instanceof TabularCopySource &&
                    (source as TabularCopySource).mode === 'query' &&
                    missingQuery(source as TabularCopySource, sourceDataset as TabularTypeDataset)
                ) {
                    violations.push({
                        context: activity,
                        messageType: 'missing',
                        message: () =>
                            format(
                                stringResources.RequiredTemplate,
                                datasetConfig.queryDisplayName || datasetConfig.queryAlias || stringResources.QueryLabel
                            ),
                        redirectTo: {
                            entity: activity,
                            attribute: TabularCopySource.READER_QUERY
                        }
                    });
                }
            }
        }

        // validate partition
        const copySourceModel = Metamodel.modelOf(source);
        // validate netezza partition option only when enableNetezzaV2 is true
        if (
            copySourceModel.hasAttribute('partitionOption') &&
            copySourceModel.name !== adfModelTypeMap.netezza.copySourceType
        ) {
            // 1. validate netezza v1 dataset
            if (
                copySourceModel.attribute('partitionOption').templateValue(source) != null ||
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (!stringIsNullOrEmpty((source as any).partitionOption) &&
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (source as any).partitionOption !== partitionOptionConst.none)
            ) {
                if (sourceDataset instanceof NetezzaTableDataset && !sourceDataset.isV2) {
                    violations.push({
                        context: activity,
                        message: () => stringResources.ParallelCopyNotSupportsOldNetezzaDataset_ErrorMessage,
                        redirectTo: {
                            entity: activity,
                            attribute: NetezzaCopySource.PARTITION_OPTION
                        }
                    });
                }
            }
            // 2. validate partition when use query mode
            if (
                copySourceModel.attribute('partitionOption').templateValue(source) == null && // parition is not expression
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                !stringIsNullOrEmpty((source as any).partitionOption) &&
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (source as any).partitionOption !== partitionOptionConst.none
            ) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                if ((source as any).mode === 'query' && sourceDataset instanceof TabularTypeDataset) {
                    // only do this validation when there is no table specified in dataset
                    if (
                        !TabularCopySource.READER_QUERY.templateValue(source) && // query is not expression
                        !stringIsNullOrEmpty((source as TabularCopySource).readerQuery)
                    ) {
                        const query: string = (source as TabularCopySource).readerQuery;
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const partitionOption: string = (source as any).partitionOption;
                        violations.push(
                            ...validateQueryForPartitionOption(partitionOption, query, activity, copySourceModel)
                        );
                    }
                    // BE request: validatePartitionSettingsProperties only when dataset has no table settin since in that case, BE isn't able to detect partition column name only by a query setting
                    if (
                        !hasTableNameOrTable(sourceDataset) ||
                        copySourceModel.name === adfModelTypeMap.sapHana.copySourceType
                    ) {
                        violations.push(...validatePartitionSettingsProperties(source, activity));
                    }
                }
                if (
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (source as any).mode === 'table' &&
                    copySourceModel.name === adfModelTypeMap.sapHana.copySourceType
                ) {
                    violations.push(...validatePartitionSettingsProperties(source, activity));
                }
            }
        }
    }
    return violations;
}

export async function validateSourceDataset(
    activity: Activity,
    sourceDataset: Dataset,
    bypassRowDelimiterValidation: boolean = false,
    bypassColumnDelimiterValidation: boolean = false,
    bypassCompressionValidation: boolean = false,
    context: IValidationContext
): Promise<IViolation[] | undefined> {
    let violations: IViolation[] = [];
    if (sourceDataset != null) {
        // validate activity supportablity
        const activitySupportabilityViolations = await validateActivitySupportability(
            activity,
            sourceDataset,
            false,
            context
        );
        if (activitySupportabilityViolations != null && activitySupportabilityViolations.length > 0) {
            violations = violations.concat(activitySupportabilityViolations);
        }

        if (sourceDataset instanceof DelimitedTextDataset) {
            violations.push(
                ...(await validateDelimiterForDelimitedText(
                    sourceDataset,
                    activity,
                    bypassRowDelimiterValidation,
                    bypassColumnDelimiterValidation
                ))
            );
            if (!bypassCompressionValidation) {
                violations.push(...(validateCompressionForDelimitedText(sourceDataset, activity) || []));
            }
        }

        if (sourceDataset instanceof OrcDataset && sourceDataset.compressionCodec === compressionCodecType.lzo) {
            violations.push({
                message: () =>
                    sanitizeAndFormatString(
                        stringResources.Dataset_FileFormat_CopyCompressionNotSupport,
                        sourceDataset.compressionCodec,
                        activity.name
                    ),
                redirectTo: {
                    entity: sourceDataset,
                    attribute: OrcDataset.COMPRESSION_CODEC
                }
            });
        }
    }
    return violations;
}

export async function validateActivitySupportability(
    activity: Activity,
    dataset: Dataset,
    isCopySink: boolean,
    context: IValidationContext
): Promise<IViolation[]> {
    if (!activity || !dataset) {
        return [];
    }
    const violations: IViolation[] = [];
    const datasetConfig = (
        await getServiceInstance<DatasetRegistryService>(context.contextData, 'DatasetRegistryService')
    ).getDatasetConfiguration(Metamodel.modelOf(dataset).name);

    const activityModelName = Metamodel.modelOf(activity).name;
    switch (activityModelName) {
        case 'Copy':
            if (
                !isCopySink &&
                datasetConfig.copySourceRequiredProperties &&
                datasetConfig.copySourceRequiredProperties.length > 0
            ) {
                validateActivityRequiredProperties(
                    activity,
                    datasetConfig.copySourceRequiredProperties,
                    violations,
                    dataset
                );
            }
            if (
                isCopySink &&
                datasetConfig.copySinkRequiredProperties &&
                datasetConfig.copySinkRequiredProperties.length > 0
            ) {
                validateActivityRequiredProperties(
                    activity,
                    datasetConfig.copySinkRequiredProperties,
                    violations,
                    dataset
                );
            }
            break;
        case 'GetMetadata':
            if (
                datasetConfig.supportGetMetadataActivity &&
                datasetConfig.getMetadataActivityRequiredProperties &&
                datasetConfig.getMetadataActivityRequiredProperties.length > 0
            ) {
                validateActivityRequiredProperties(
                    activity,
                    datasetConfig.getMetadataActivityRequiredProperties,
                    violations,
                    dataset
                );
            }
            break;
        case 'Lookup':
            if (
                !datasetConfig.notSupportLookupActivity &&
                datasetConfig.lookupActivityRequiredProperties &&
                datasetConfig.lookupActivityRequiredProperties.length > 0
            ) {
                validateActivityRequiredProperties(
                    activity,
                    datasetConfig.lookupActivityRequiredProperties,
                    violations,
                    dataset
                );
            }
            break;
        case 'Delete':
            if (
                datasetConfig.supportDeleteActivity &&
                datasetConfig.deleteActivityRequiredProperties &&
                datasetConfig.deleteActivityRequiredProperties.length > 0
            ) {
                validateActivityRequiredProperties(
                    activity,
                    datasetConfig.deleteActivityRequiredProperties,
                    violations,
                    dataset
                );
            }
            break;
        default:
            break;
    }
    return violations;
}
/**
 * @param activity validation owner
 * @param requiredProperties requiredProperties of dataset when referenced in activity
 * @param violations validate result
 * @param dataset sourceDataset in activity, set it when we need to validate sourceDataset, will take effects only when activitySupportability.requiredProperties.asSource is not null
 */
export function validateActivityRequiredProperties(
    activity: Activity,
    requiredProperties: string[],
    violations: IViolation[],
    dataset?: Dataset
): void {
    if (requiredProperties != null) {
        requiredProperties.forEach((requiredProperty) => {
            const [entity, attribute] =
                dataset && Metamodel.modelOf(dataset).hasAttribute(requiredProperty)
                    ? [dataset, Metamodel.modelOf(dataset).attribute(requiredProperty)]
                    : [undefined, undefined];
            if (entity != null && attribute != null) {
                if (
                    attribute.templateValue(entity) == null &&
                    (attribute.value(entity) == null ||
                        (typeof attribute.value(entity) === 'string' && stringIsNullOrEmpty(attribute.value(entity))))
                ) {
                    violations.push({
                        context: activity,
                        message: () =>
                            sanitizeAndFormatString(
                                stringResources.PropertyRequired_InActivity_Template,
                                attribute.display || attribute.name,
                                activity.name
                            ),
                        redirectTo: {
                            entity: dataset,
                            attribute
                        }
                    });
                }
            }
        });
    }
}

export function validatePathInStagingAndLoggingSettings(setting: StagingSettings | LoggingLocation): IViolation[] {
    if (setting.storageLinkedService == null) {
        return [];
    }

    if (
        (setting.storageLinkedService instanceof AzureStorageLinkedService ||
            setting.storageLinkedService instanceof AzureBlobFSLinkedService) &&
        !stringIsNullOrEmpty(setting.path) &&
        !Constants.blobContainerNameValidation.test(setting.path)
    ) {
        return [
            {
                message: () => stringResources.CopyWizard_Dataset_Blob_Sink_ContainerValidationError
            }
        ];
    }
    return [];
}

export function getDatasetRequiredIRVersion(linkedServiceMetadata: ILinkedServiceTypeMetadata): string {
    return linkedServiceMetadata.supportIRVersion;
}

export function getRuntimeSelfHostedIR(
    sourceDataset: Dataset,
    sinkDataset?: Dataset,
    sourceLinkedservice?: LinkedService | SqlPool,
    sinkLinkedservice?: LinkedService | SqlPool,
    stagingLS?: LinkedService
): SelfHostedIntegrationRuntime {
    const sourceLS = (sourceDataset && sourceDataset.linkedService) || sourceLinkedservice;
    const sinkLS = (sinkDataset && sinkDataset.linkedService) || sinkLinkedservice;
    const runtimeSHIRs = getRuntimeIRs(sourceLS, sinkLS, stagingLS).filter(
        (irMap) => irMap.ir instanceof SelfHostedIntegrationRuntime
    );
    return runtimeSHIRs.length > 0 ? runtimeSHIRs[0].ir : undefined; // if there are two returned, they have to be the same.
}

export function getRuntimeManagedVNetIR(
    sourceDataset: Dataset,
    sinkDataset?: Dataset,
    sourceLinkedservice?: LinkedService | SqlPool,
    sinkLinkedservice?: LinkedService | SqlPool,
    stagingLS?: LinkedService
): ManagedIntegrationRuntime[] {
    const sourceLS = (sourceDataset && sourceDataset.linkedService) || sourceLinkedservice;
    const sinkLS = (sinkDataset && sinkDataset.linkedService) || sinkLinkedservice;
    const runtimeIRs = getRuntimeIRs(sourceLS, sinkLS, stagingLS);
    return runtimeIRs
        .filter((irMap) => irMap != null && irMap.irType === irTypesConst.ManagedVNet)
        .map((irMap) => irMap.ir);
}

function getRuntimeIRs(
    sourceLS: LinkedService | SqlPool,
    sinkLS: LinkedService | SqlPool,
    stagingLS: LinkedService
): IRuntimeIRMap[] {
    const sourceIR = sourceLS instanceof LinkedService ? sourceLS.integrationRuntime : undefined;
    const sinkIR = sinkLS instanceof LinkedService ? sinkLS.integrationRuntime : undefined;
    const stagingIR = stagingLS && stagingLS.integrationRuntime;

    if (stagingLS != null) {
        return [getRuntimeIR(sourceIR, stagingIR), getRuntimeIR(stagingIR, sinkIR)];
    }
    return [getRuntimeIR(sourceIR, sinkIR)];
}

function getRuntimeIR(sourceIR: IntegrationRuntime, sinkIR: IntegrationRuntime): IRuntimeIRMap {
    const sourceIRType = IntegrationRuntimeAdapters.GetIRTypeFromLocalModel(sourceIR);
    const sinkIRType = IntegrationRuntimeAdapters.GetIRTypeFromLocalModel(sinkIR);

    const sourceWin =
        sinkIR == null ||
        sourceIR instanceof SelfHostedIntegrationRuntime || // even sink could also be SHIR in this case, but they have to be the same
        (sourceIRType === irTypesConst.ManagedVNet &&
            !(sinkIR instanceof SelfHostedIntegrationRuntime) &&
            sinkIRType !== irTypesConst.ManagedVNet);
    return sourceWin
        ? {
            ir: sourceIR,
            irType: sourceIRType
        }
        : {
            ir: sinkIR,
            irType: sinkIRType
        };
}

export async function validateDelimiterForDelimitedText(
    dataset: DelimitedTextDataset,
    owner: Named,
    bypassRowDelimiterValidation: boolean = false,
    bypassColumnDelimiterValidation: boolean = false
): Promise<IViolation[] | undefined> {
    // in copy activity and lookup activity, rowDelimiter and columnDelimiter cannot be empty string or multi-char string
    const violations: IViolation[] = [];
    if (bypassRowDelimiterValidation) {
        // in direct polybase, only empty string is not allowed, this is already handled in direct polybase validation
    } else if (
        dataset.rowDelimiter != null &&
        (dataset.rowDelimiter === '' || (!__IS_FABRIC_DPE__ && !/^(\\u([0-9a-f]){4}|[^])$/.test(dataset.rowDelimiter)))
    ) {
        violations.push({
            // Trident now supports multi-char row delimiter
            message: () =>
                sanitizeAndFormatString(
                    __IS_FABRIC_DPE__
                        ? stringResources.FileFormat_DelimiterCannotBeNoneErrorRef
                        : stringResources.Dataset_FileFormat_DelimiterCanOnlyBeOneErrorRef,
                    stringResources.Dataset_FileFormat_RowDelimiterFieldLabel,
                    owner.name
                ),
            redirectTo: {
                entity: dataset,
                attribute: DelimitedTextDataset.ROW_DELIMITER
            }
        });
    }
    if (bypassColumnDelimiterValidation) {
        // in direct polybase, only empty string is not allowed, this is already handled in direct polybase validation
    } else if (
        dataset.columnDelimiter != null &&
        (dataset.columnDelimiter === '' || !/^(\\u([0-9a-f]){4}|[^])$/.test(dataset.columnDelimiter))
    ) {
        if (dataset.columnDelimiter === '') {
            violations.push({
                message: () =>
                    sanitizeAndFormatString(
                        __IS_FABRIC_DPE__
                            ? stringResources.FileFormat_DelimiterCannotBeNoneErrorRef
                            : stringResources.Dataset_FileFormat_DelimiterCannotBeNoneErrorRef,
                        stringResources.ModelName_ColumnDelimiter,
                        owner.name
                    ),
                redirectTo: {
                    entity: dataset,
                    attribute: DelimitedTextDataset.COLUMN_DELIMITER
                }
            });
        }
        // check duplication against rowDelimiter, quoteChar, escapeChar
        // since only colDelimiter supports multiple characters,
        // we can simply check if other chars are part of colDelimiter for the time being
        const checkList = [
            {
                char: dataset.rowDelimiter,
                name: stringResources.Dataset_FileFormat_RowDelimiterFieldLabel.toLocaleLowerCase()
            },
            {
                char: dataset.escapeChar,
                name: stringResources.Dataset_FileFormat_EscapeCharFieldLabel.toLocaleLowerCase()
            },
            {
                char: dataset.quoteChar,
                name: stringResources.Dataset_FileFormat_QuoteCharFieldLabel.toLocaleLowerCase()
            }
        ];
        checkList.forEach((item) => {
            if (!stringIsNullOrEmpty(item.char) && dataset.columnDelimiter.indexOf(item.char) > -1) {
                violations.push({
                    message: () =>
                        sanitizeAndFormatString(
                            stringResources.Dataset_FileFormat_DupCharWithColDelimiter,
                            item.char,
                            item.name
                        ),
                    redirectTo: {
                        entity: dataset,
                        attribute: DelimitedTextDataset.COLUMN_DELIMITER
                    }
                });
            }
        });
        // colDelimiter cannot equally to nullValue
        if (dataset.columnDelimiter === dataset.nullValue) {
            violations.push({
                message: () => stringResources.Dataset_FileFormat_ColDelimiterNotSameAsNullValue,
                redirectTo: {
                    entity: dataset,
                    attribute: DelimitedTextDataset.COLUMN_DELIMITER
                }
            });
        }
    }
    return violations;
}

export function validateCompressionForDelimitedText(
    dataset: DelimitedTextDataset,
    owner: Named,
    isDataflow?: boolean
): IViolation[] | undefined {
    if (!isDataflow && [compressionCodecType.snappy, compressionCodecType.lz4].indexOf(dataset.compressionCodec) >= 0) {
        return [
            {
                message: () =>
                    sanitizeAndFormatString(
                        stringResources.Dataset_FileFormat_CopyCompressionNotSupport,
                        dataset.compressionCodec,
                        owner.name
                    ),
                redirectTo: {
                    entity: dataset,
                    attribute: DelimitedTextDataset.COMPRESSION_CODEC
                }
            }
        ];
    }
    if (isDataflow && [compressionCodecType.ZipDeflate].indexOf(dataset.compressionCodec) >= 0) {
        return [
            {
                message: () =>
                    sanitizeAndFormatString(
                        stringResources.Dataset_FileFormat_DataflowCompressionNotSupport,
                        dataset.compressionCodec
                    ),
                redirectTo: {
                    entity: dataset,
                    attribute: DelimitedTextDataset.COMPRESSION_CODEC
                }
            }
        ];
    }
    return undefined;
}

export function validateQueryForPartitionOption(
    partitionOption: string,
    query: string,
    activity: Activity,
    copySourceModel: IModel
): IViolation[] {
    const violations: IViolation[] = [];
    if (partitionOption === partitionOptionConst.dyamicRange) {
        if (
            copySourceModel.name === adfModelTypeMap.teradata.copySourceType ||
            copySourceModel.name === adfModelTypeMap.oracle.copySourceType ||
            copySourceModel.name === adfModelTypeMap.netezza.copySourceType ||
            copySourceModel.name === adfModelTypeMap.azurePostgreSQL.copySourceType ||
            copySourceModel.name === adfModelTypeMap.amazonRdsForOracle.copySourceType
        ) {
            if (copySourceModel.name === adfModelTypeMap.oracle.copySourceType ||
                copySourceModel.name === adfModelTypeMap.amazonRdsForOracle.copySourceType) {
                // Let (RDS for) Oracle v1 in ADF also allow 'Df' when (RDS for) Oracle v2 is enabled
                if (
                    (query.indexOf('?DfRangePartitionColumnName') < 0 &&
                        query.indexOf('?AdfRangePartitionColumnName') < 0) ||
                    (query.indexOf('?DfRangePartitionUpbound') < 0 && query.indexOf('?AdfRangePartitionUpbound') < 0) ||
                    (query.indexOf('?DfRangePartitionLowbound') < 0 && query.indexOf('?AdfRangePartitionLowbound') < 0)
                ) {
                    violations.push({
                        context: activity,
                        message: () =>
                            format(
                                stringResources.PartitionValidation_Query_DynamicRange_ErrorMessage_Template,
                                '?DfRangePartitionColumnName, ?DfRangePartitionLowbound, ?DfRangePartitionUpbound'
                            ),
                        redirectTo: {
                            entity: activity,
                            attribute: copySourceModel.attribute('readerQuery')
                        }
                    });
                }
            } else if (
                query.indexOf('?AdfRangePartitionColumnName') < 0 ||
                query.indexOf('?AdfRangePartitionUpbound') < 0 ||
                query.indexOf('?AdfRangePartitionLowbound') < 0
            ) {
                // teradata, azurePostgreSQL and netezza in adf
                violations.push({
                    context: activity,
                    message: () =>
                        format(
                            stringResources.PartitionValidation_Query_DynamicRange_ErrorMessage_Template,
                            '?AdfRangePartitionColumnName, ?AdfRangePartitionLowbound, ?AdfRangePartitionUpbound'
                        ),
                    redirectTo: {
                        entity: activity,
                        attribute: copySourceModel.attribute('readerQuery')
                    }
                });
            }
        } else if (
            query.indexOf('?AdfDynamicRangePartitionCondition') < 0 &&
            query.indexOf('?DfDynamicRangePartitionCondition') < 0
        ) {
            // azure sql, sql server, sqldw, sqlmi
            violations.push({
                context: activity,
                message: () =>
                    format(
                        stringResources.PartitionValidation_Query_DynamicRange_ErrorMessage_Template,
                        '?DfDynamicRangePartitionCondition'
                    ),
                redirectTo: {
                    entity: activity,
                    attribute: copySourceModel.attribute('readerQuery')
                }
            });
        }
        // oracle and azurePostgreSQL
    } else if (partitionOption === partitionOptionConst.physicalPartitionsOfTable) {
        if (
            copySourceModel.name === adfModelTypeMap.oracle.copySourceType ||
            copySourceModel.name === adfModelTypeMap.azurePostgreSQL.copySourceType
        ) {
            if (copySourceModel.name === adfModelTypeMap.oracle.copySourceType) {
                // Let Oracle v1 in ADF also allow 'Df' when Oracle v2 is enabled
                if (query.indexOf('?DfTabularPartitionName') < 0 && query.indexOf('?AdfTabularPartitionName') < 0) {
                    violations.push({
                        context: activity,
                        message: () =>
                            format(
                                stringResources.PartitionValidation_Query_Physical_ErrorMessage,
                                '?DfTabularPartitionName'
                            ),
                        redirectTo: {
                            entity: activity,
                            attribute: copySourceModel.attribute('readerQuery')
                        }
                    });
                }
            } else if (query.indexOf('?AdfTabularPartitionName') < 0) {
                violations.push({
                    context: activity,
                    message: () =>
                        format(
                            stringResources.PartitionValidation_Query_Physical_ErrorMessage,
                            '?AdfTabularPartitionName'
                        ),
                    redirectTo: {
                        entity: activity,
                        attribute: copySourceModel.attribute('readerQuery')
                    }
                });
            }
        }
        // teradata
    } else if (
        partitionOption === partitionOptionConst.hash &&
        copySourceModel.name === adfModelTypeMap.teradata.copySourceType
    ) {
        if (query.indexOf('?AdfHashPartitionCondition') < 0) {
            violations.push({
                context: activity,
                message: () => stringResources.PartitionValidation_Query_Hash_ErrorMessage,
                redirectTo: {
                    entity: activity,
                    attribute: copySourceModel.attribute('readerQuery')
                }
            });
        }
        // netezza
    } else if (
        partitionOption === partitionOptionConst.dataSlice &&
        copySourceModel.name === adfModelTypeMap.netezza.copySourceType
    ) {
        if (query.indexOf('?AdfPartitionCount') < 0 || query.indexOf('?AdfDataSliceCondition') < 0) {
            violations.push({
                context: activity,
                message: () => stringResources.PartitionValidation_Query_DataSlice_ErrorMessage,
                redirectTo: {
                    entity: activity,
                    attribute: copySourceModel.attribute('readerQuery')
                }
            });
        }
        // SAP HANA
    } else if (partitionOption === partitionOptionConst.sapHanaDynamicRange) {
        if (query.indexOf('?AdfHanaDynamicRangePartitionCondition') < 0) {
            violations.push({
                context: activity,
                message: () => stringResources.PartitionValidation_Query_SAPHANADynamicRange_ErrorMessage,
                redirectTo: {
                    entity: activity,
                    attribute: copySourceModel.attribute('readerQuery')
                }
            });
        }
    }
    return violations;
}

export function validatePartitionSettingsProperties(copySource: CopySource, activity: Activity): IViolation[] {
    const copySourceModel = Metamodel.modelOf(copySource);
    const violations: IViolation[] = [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const partitionOption = (copySource as any).partitionOption;
    if (partitionOption === partitionOptionConst.dyamicRange) {
        // teradata, oracle, azurePostgreSQL, netezza and sql source
        const columnNameAttr = copySourceModel.attribute('partitionColumnName');
        if (
            columnNameAttr.templateValue(copySource) == null &&
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            stringIsNullOrEmpty((copySource as any).partitionColumnName)
        ) {
            violations.push({
                context: activity,
                message: () => format(stringResources.RequiredTemplate, columnNameAttr.display),
                redirectTo: {
                    entity: activity,
                    attribute: columnNameAttr
                }
            });
        }
        // teradata, oracle, azurePostgreSQL and netezza
        if (
            copySourceModel.name === adfModelTypeMap.teradata.copySourceType ||
            copySourceModel.name === adfModelTypeMap.oracle.copySourceType ||
            copySourceModel.name === adfModelTypeMap.netezza.copySourceType ||
            copySourceModel.name === adfModelTypeMap.azurePostgreSQL.copySourceType
        ) {
            const upperBoundAttr = copySourceModel.attribute('partitionUpperBound');
            if (
                upperBoundAttr.templateValue(copySource) == null &&
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                stringIsNullOrEmpty((copySource as any).partitionUpperBound)
            ) {
                violations.push({
                    context: activity,
                    message: () => format(stringResources.RequiredTemplate, upperBoundAttr.display),
                    redirectTo: {
                        entity: activity,
                        attribute: upperBoundAttr
                    }
                });
            }
            const lowerBoundAttr = copySourceModel.attribute('partitionLowerBound');
            if (
                lowerBoundAttr.templateValue(copySource) == null &&
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                stringIsNullOrEmpty((copySource as any).partitionLowerBound)
            ) {
                violations.push({
                    context: activity,
                    message: () => format(stringResources.RequiredTemplate, lowerBoundAttr.display),
                    redirectTo: {
                        entity: activity,
                        attribute: lowerBoundAttr
                    }
                });
            }
        }
        // oracle and azurePostgreSQL
    } else if (
        partitionOption === partitionOptionConst.physicalPartitionsOfTable &&
        (copySourceModel.name === adfModelTypeMap.oracle.copySourceType ||
            copySourceModel.name === adfModelTypeMap.azurePostgreSQL.copySourceType)
    ) {
        const partitionNamesAttr = copySourceModel.attribute('partitionNames');
        if (
            partitionNamesAttr.templateValue(copySource) == null &&
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ((copySource as any).partitionNames == null || (copySource as any).partitionNames.length <= 0)
        ) {
            violations.push({
                context: activity,
                message: () => format(stringResources.RequiredTemplate, partitionNamesAttr.display),
                redirectTo: {
                    entity: activity,
                    attribute: partitionNamesAttr
                }
            });
        }
        // teradata
    } else if (
        partitionOption === partitionOptionConst.hash &&
        copySourceModel.name === adfModelTypeMap.teradata.copySourceType
    ) {
        const columnNameAttr = copySourceModel.attribute('partitionColumnName');
        if (
            columnNameAttr.templateValue(copySource) == null &&
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            stringIsNullOrEmpty((copySource as any).partitionColumnName)
        ) {
            violations.push({
                context: activity,
                message: () => format(stringResources.RequiredTemplate, columnNameAttr.display),
                redirectTo: {
                    entity: activity,
                    attribute: columnNameAttr
                }
            });
        }
        // hana
    } else if (partitionOption === partitionOptionConst.sapHanaDynamicRange) {
        const columnNameAttr = copySourceModel.attribute('partitionColumnName');
        if (
            columnNameAttr.templateValue(copySource) == null &&
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            stringIsNullOrEmpty((copySource as any).partitionColumnName)
        ) {
            violations.push({
                context: activity,
                message: () => format(stringResources.RequiredTemplate, columnNameAttr.display),
                redirectTo: {
                    entity: activity,
                    attribute: columnNameAttr
                }
            });
        }
    }

    return violations;
}

export function validateFilePath(activity?: Activity, arg?: FilePath, attribute?: IAttribute): IViolation[] {
    const violations: IViolation[] = [];
    if (activity && arg && (arg.path == null || arg.path === '')) {
        const model = Metamodel.modelOf(activity);
        const activityName = activity.name ? '\'' + activity.name + '\'' : '';
        violations.push({
            context: activity,
            message: () =>
                sanitizeAndFormatString(
                    stringResources.FilePathValidationMessage,
                    model.display[0],
                    activityName,
                    attribute.display
                ),
            attributes: [attribute]
        });
    }
    return violations;
}
/**
 * Check if copy source is in wildcard path mode
 * This affects if we should further check wildcard folder path and file name
 */
export function isFileFilterPathMode(readSettings: FileServerStoreReadSettings): boolean {
    const model = Metamodel.modelOf(readSettings);
    const filePathModeAttr = model.attribute('filePathMode');
    if (filePathModeAttr && filePathModeAttr.value(readSettings) === FilePathMode.FILEFILTER) {
        return true;
    }
    return false;
}

/**
 * Check if copy source is in wildcard path mode
 * This affects if we should further check wildcard folder path and file name
 */
export function isWildCardPathMode(readSettings: BlobTypeStoreReadSettings): boolean {
    const model = Metamodel.modelOf(readSettings);
    const filePathModeAttr = model.attribute('filePathMode');
    if (filePathModeAttr && filePathModeAttr.value(readSettings) === FilePathMode.WILDCARD) {
        // do not check wildcard calue if it's not in WILDCARD mode
        return true;
    }
    return false;
}

/**
 * Get wildcard filename value
 */
export function getWildcardValue(
    readSettings: BlobTypeStoreReadSettings,
    propertyName: 'wildcardFileName' | 'wildcardFolderPath',
    getTemplatedValue?: boolean
): string {
    if (readSettings != null) {
        const model = Metamodel.modelOf(readSettings);
        return !getTemplatedValue
            ? model.attribute(propertyName).value(readSettings)
            : model.attribute(propertyName).templateValue(readSettings);
    }
    return undefined;
}

export function wildcardFileNameIsNull(
    readSettings: BlobTypeStoreReadSettings,
    treatExpressionValueAsNull?: boolean
): boolean {
    if (readSettings != null && isWildCardPathMode(readSettings)) {
        const fileNameValueIsNull = stringIsNullOrEmpty(getWildcardValue(readSettings, 'wildcardFileName'));
        if (treatExpressionValueAsNull) {
            return fileNameValueIsNull;
        } else {
            const fileNameTemplateValueIsNull = stringIsNullOrEmpty(
                getWildcardValue(readSettings, 'wildcardFileName', true)
            );
            return fileNameValueIsNull && fileNameTemplateValueIsNull;
        }
    }
    return true;
}

export function wildcardFolderPathIsNull(
    readSettings: BlobTypeStoreReadSettings,
    treatExpressionValueAsNull?: boolean
): boolean {
    if (readSettings != null && isWildCardPathMode(readSettings)) {
        const fileNameValueIsNull = stringIsNullOrEmpty(getWildcardValue(readSettings, 'wildcardFolderPath'));
        if (treatExpressionValueAsNull) {
            return fileNameValueIsNull;
        } else {
            const fileNameTemplateValueIsNull = stringIsNullOrEmpty(
                getWildcardValue(readSettings, 'wildcardFolderPath', true)
            );
            return fileNameValueIsNull && fileNameTemplateValueIsNull;
        }
    }
    return true;
}

export function fileFilterIsNull(
    readSettings: FileServerStoreReadSettings,
    treatExpressionValueAsNull?: boolean
): boolean {
    if (readSettings != null && isFileFilterPathMode(readSettings)) {
        const model = Metamodel.modelOf(readSettings);
        const fileNameValueIsNull = stringIsNullOrEmpty(model.attribute('fileFilter').value(readSettings));
        const fileNameTemplateValueIsNull = stringIsNullOrEmpty(
            model.attribute('fileFilter').templateValue(readSettings)
        );
        return treatExpressionValueAsNull ? fileNameValueIsNull : fileNameValueIsNull && fileNameTemplateValueIsNull;
    }
    return true;
}

export function modifiedTimeIsNull(readSettings: BlobTypeStoreReadSettings, checkExpression?: boolean): boolean {
    if (readSettings != null) {
        const model = Metamodel.modelOf(readSettings);
        const filePathModeAttr = model.attribute('filePathMode');
        if (filePathModeAttr && filePathModeAttr.value(readSettings) !== FilePathMode.LIST) {
            const modifiedDatetimeStartAttr = model.attribute('modifiedDatetimeStart');
            const modifiedDatetimeEndAttr = model.attribute('modifiedDatetimeEnd');
            const valueIsNull =
                modifiedDatetimeStartAttr.value(readSettings) == null &&
                modifiedDatetimeEndAttr.value(readSettings) == null;
            const templateValueIsNull =
                !modifiedDatetimeStartAttr.templateValue(readSettings) &&
                !modifiedDatetimeEndAttr.templateValue(readSettings);
            return checkExpression ? templateValueIsNull && valueIsNull : valueIsNull;
        }
    }
    return true;
}

export function prefixPathIsNull(readSettings: BlobTypeStoreReadSettings): boolean {
    if (readSettings != null) {
        const model = Metamodel.modelOf(readSettings);
        if (model.hasAttribute('prefix')) {
            const filePathModeAttr = model.attribute('filePathMode');
            if (filePathModeAttr && filePathModeAttr.value(readSettings) !== FilePathMode.PREFIX) {
                return true;
            }
            return stringIsNullOrEmpty(model.attribute('prefix').value(readSettings));
        }
    }
    return true;
}
